import type { AAPlugin } from '@particle-network/aa-plugin';
import React, { createContext, useMemo, type ReactNode } from 'react';
import ConnectModal from '../components/connectModal';
import { type Config } from '../createConfig';
import { useAppearance } from '../hooks/useAppearance';
import { getAuthCoreTheme } from '../styles';
import type { AuthWalletConnectorProperties } from '../types/properties';
import { getSystemMode } from '../utils/theme';
import { ModalProvider } from './modalProvider';
import type { ConnectKitOptions } from './types';
import { Web3Provder } from './web3Provider';

type ContextValue = {
  options: ConnectKitOptions;
  config: Config;
};

export const Context = createContext<ContextValue | null>(null);

export type ConnectKitProviderProps = {
  children?: ReactNode;
  config: Config;
  reconnectOnMount?: boolean | undefined;
};

export const ConnectKitProvider = (parameters: ConnectKitProviderProps): React.ReactElement => {
  // Only allow for mounting ConnectKitProvider once, so we avoid weird global
  // state collisions.
  if (React.useContext(Context)) {
    throw new Error('Multiple, nested usages of ConnectKitProvider detected. Please use only one.');
  }

  const { children, config, reconnectOnMount = true } = parameters;
  const { appearance } = useAppearance(config);

  const { AuthCoreContextProvider, authOptions } = useMemo(() => {
    const authWalletConnector = config.getWalletConnector<AuthWalletConnectorProperties>('particleAuth');
    const aaPlugin = config.plugins.find((plugin) => plugin.id === 'aa') as AAPlugin;
    const erc4337 = aaPlugin ? aaPlugin.erc4337 : undefined;

    let theme = appearance?.mode ?? getSystemMode();
    if (theme === 'auto') {
      theme = getSystemMode();
    }

    return {
      AuthCoreContextProvider: authWalletConnector?.AuthCoreContextProvider ?? React.Fragment,
      authOptions: authWalletConnector
        ? {
            options: {
              themeType: theme,
              ...authWalletConnector.authOptions,
              customStyle: {
                ...authWalletConnector.authOptions?.customStyle,
                theme: {
                  [theme as string]: getAuthCoreTheme(appearance),
                },
              },
              erc4337,
            },
          }
        : undefined,
    };
  }, [config, appearance]);

  return (
    <Context.Provider value={useMemo(() => ({ options: config.options, config }), [config])}>
      <AuthCoreContextProvider {...authOptions}>
        <Web3Provder>
          {children}
          <ModalProvider reconnectOnMount={reconnectOnMount}>
            <ConnectModal />
          </ModalProvider>
        </Web3Provder>
      </AuthCoreContextProvider>
    </Context.Provider>
  );
};

export const useContext = () => {
  const context = React.useContext(Context);
  if (!context) throw Error('ConnectKit Hook must be inside a Provider.');
  return context;
};
